import { useEffect, useState } from 'react';
import { dashboardT } from '../../../../lib/messages';

export function DashboardScrollNavigation({
  sections,
}: {
  sections: readonly { id: string; label: string }[];
}) {
  const [active, setActive] = useState(sections[0]?.id ?? '');
  const ids = sections.map(({ id }) => id).join(',');
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const elements = ids.split(',').flatMap((id) => {
      const element = document.getElementById(id);
      return element ? [element] : [];
    });
    const visible = new Set<Element>();
    const last = elements.at(-1);
    const scroller = last ? scrollParent(last) : null;
    // A short last section never reaches the trigger line, so the bottom of the scroll selects it.
    const atBottom = () => {
      const box = scroller ?? document.documentElement;
      const scrollable = box.scrollHeight - box.clientHeight > 2;
      return scrollable && box.scrollHeight - box.scrollTop - box.clientHeight <= 2;
    };
    const update = () => {
      const current = atBottom() ? last : elements.filter((element) => visible.has(element)).at(-1);
      if (current) setActive(current.id);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        update();
      },
      // A percentage margin follows the viewport height, so resizing needs no new observer.
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );
    elements.forEach((element) => {
      observer.observe(element);
    });
    const scrollTarget: HTMLElement | Window = scroller ?? window;
    scrollTarget.addEventListener('scroll', update, { passive: true });
    return () => {
      observer.disconnect();
      scrollTarget.removeEventListener('scroll', update);
    };
  }, [ids]);
  const activeIndex = Math.max(
    0,
    sections.findIndex(({ id }) => id === active),
  );
  return (
    <nav
      aria-label={dashboardT('dashboard.workspace.navigation')}
      className="bg-background sticky top-20 z-20 hidden max-w-36 min-w-0 flex-col gap-2 self-start py-4 xl:flex"
    >
      {/* One mark for the whole rail, moved by a compositor-only transform. Items sit on a
          3rem pitch (py-2 + h-6 + gap-2), so the mark needs no measuring. */}
      <span
        aria-hidden="true"
        className="bg-primary ease-fluid pointer-events-none absolute top-6 left-0 h-6 w-0.5 rounded-full transition-transform duration-[var(--duration-fluid)] motion-reduce:transition-none"
        style={{ transform: `translateY(${String(activeIndex * 3)}rem)` }}
      />
      {sections.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          aria-current={active === id ? 'location' : undefined}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring aria-[current=location]:text-foreground flex shrink-0 items-center gap-2 rounded py-2 pr-1 text-xs transition-colors focus-visible:ring-2"
        >
          <span aria-hidden="true" className="bg-border h-6 w-0.5 shrink-0 rounded-full" />
          <span className="truncate">{label}</span>
        </a>
      ))}
    </nav>
  );
}

function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}
